import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { columnPropType, tableCategoriesPropType } from '../utils/propTypes';
import { AsyncDataGridContext } from './context';
import asyncDataGridReducer from '../asyncStateReducer/reducer';
import { assembleRows, createCacheKey } from '../asyncStateReducer/utils';
import { setDataGridState } from '../asyncStateReducer/actions';
const propTypes = {
  /**
   * Row grouping metadata for tables
   * array of category objects
   *
   * ```
   * {
   *   key: string,
   *   label: string,
   *   color: optional string - one of (dark-blue, blue, noble-blue, plum, iris, berry, coral),
   *   subCategories: optional array of category objects
   * }
   * ```
   */
  categories: PropTypes.arrayOf(tableCategoriesPropType),
  /**
   * AsyncDataGrid can render the asynchronous table and pagination components as children.
   */
  children: PropTypes.node,
  /**
   * Additional classes to be added to the container
   */
  className: PropTypes.string,
  /**
   * Array of columns that define the layout and presentation of data in the grid
   */
  columns: PropTypes.arrayOf(columnPropType).isRequired,
  /**
   * Initial number of rows to show on in the data grid
   */
  defaultRowsPerPage: PropTypes.number,
  /**
   * Sets the default sort order
   */
  defaultSort: PropTypes.shape({
    key: PropTypes.string.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired
  }).isRequired,
  /**
   * Async function which accepts sort and pagination objects and returns an object containing the data to display and total records
   *
   * ```
   * // example:
   * const handleApiRequest = async (sort, pagination) => {
   *    const res = await fetch(some url here);
   *    // { data: [records here], totalRecords: some number }
   *    const returnObj = res.json();
   *
   *    return returnObj;
   * }
   * ```
   */
  handleApiRequest: PropTypes.func.isRequired,
  /**
   * Adds a custom sort function to the DataGrid. Expects a key/value pair,
   * where the key is the sort type ('number', 'string', etc.) and the value
   * is the sort function. To leverage this sort function, set the appropriate
   * `sortType` in the column configuration to the key used here.
   *
   * ```
   * // example:
   * const column = {
   *   dataKey: 'fundName',
   *   header: 'Investment',
   *   sortType: 'random',
   *   sortable: true
   * };
   * const sortTypes = {
   *   random: (a, b, dataKey) => {
   *     return Math.random() - 0.5;
   *   }
   * };
   * ```
   */
  sortTypes: PropTypes.objectOf(PropTypes.func),
  // eslint-disable-line

  /**
   * Function passed to transform raw data into usable data for the async data grid
   */
  transformFn: PropTypes.func.isRequired
};
const AsyncDataGrid = _ref => {
  let {
    categories,
    children,
    className,
    columns,
    defaultRowsPerPage = 10,
    defaultSort,
    handleApiRequest,
    transformFn
  } = _ref;
  const INIT_STATE = {
    allRows: [],
    filteredRows: [],
    visibleRows: [],
    columns: [],
    sort: defaultSort,
    pagination: {
      pageSize: defaultRowsPerPage,
      currentPage: 0,
      loading: true
    },
    categories: [],
    pageCount: 0,
    totalRecords: 0,
    filter: {},
    cache: {}
  };
  const [state, dispatch] = useReducer(asyncDataGridReducer, INIT_STATE);
  const {
    sort,
    pagination,
    filter
  } = state;
  useEffect(() => {
    const fetchInitialData = async () => {
      return await handleApiRequest(sort, {
        pageSize: pagination.pageSize,
        pageNumber: 1
      }, filter);
    };
    fetchInitialData().then(_ref2 => {
      let {
        data,
        totalRecords
      } = _ref2;
      const {
        allRows: finalRows,
        loadedRows
      } = assembleRows({
        fetchedRows: data,
        pageNumber: 1,
        pageSize: pagination.pageSize,
        totalRecords
      });
      dispatch({
        type: setDataGridState.type,
        payload: {
          allRows: finalRows,
          filteredRows: finalRows,
          visibleRows: loadedRows,
          totalRecords,
          cache: {
            [createCacheKey(sort, {
              pageSize: pagination.pageSize,
              pageNumber: 1
            }, filter)]: {
              data,
              totalRecords
            }
          }
        }
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return /*#__PURE__*/React.createElement(AsyncDataGridContext.Provider, {
    value: {
      ...state,
      columns,
      categories,
      dispatch: _ref3 => {
        let {
          type,
          payload
        } = _ref3;
        dispatch({
          type,
          payload: {
            ...(typeof payload === 'object' ? payload : {
              payload
            }),
            handleApiRequest,
            dispatch,
            transformFn,
            defaultSort
          }
        });
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('asc-grid-container', className)
  }, children));
};
AsyncDataGrid.propTypes = propTypes;
export default AsyncDataGrid;